import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "../assets/roomsCouplesLaravel.module.css";
import Loader from "../components/Loader";
import {
  downloadCSV,
  downloadPDF,
  exportWithConfirmation,
} from "../utils/exportUtils";

const RoomsCouplesLaravel = () => {
  const navigate = useNavigate();
  const [couples, setCouples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiFailed, setApiFailed] = useState(false);
  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("propertyName");
  const [sortDirection, setSortDirection] = useState("asc");
  const [visibleColumns, setVisibleColumns] = useState([
    "propertyId",
    "propertyName",
    "roomName",
    "firstGuardianName",
    "firstGuardianStatus",
    "secondGuardianName",
    "secondGuardianStatus",
    "actions",
  ]);
  const [showColDropdown, setShowColDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.column-visibility')) {
        setShowColDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Mock data for when API fails
  const getMockData = () => {
    return [
      {
        id: 1,
        propertyId: "PROP001",
        propertyName: "Sample Property 1",
        roomName: "Room 101",
        firstGuardianName: "John Smith",
        firstGuardianStatus: "Active",
        secondGuardianName: "Jane Smith",
        secondGuardianStatus: "Active",
      },
      {
        id: 2,
        propertyId: "PROP002",
        propertyName: "Sample Property 2",
        roomName: "Room 202",
        firstGuardianName: "Mike Johnson",
        firstGuardianStatus: "Active",
        secondGuardianName: "Sarah Johnson",
        secondGuardianStatus: "Active",
      },
      {
        id: 3,
        propertyId: "PROP003",
        propertyName: "Sample Property 3",
        roomName: "Room 303",
        firstGuardianName: "David Wilson",
        firstGuardianStatus: "Active",
        secondGuardianName: "Emma Wilson",
        secondGuardianStatus: "Active",
      },
    ];
  };

  useEffect(() => {
    loadCouples();
  }, []);

  const loadCouples = async () => {
    try {
      setLoading(true);
      setApiFailed(false);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/rooms/couples`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          console.log("API authentication failed, using mock data");
          setApiFailed(true);
          setCouples(getMockData());
          return;
        }
        throw new Error("Failed to load couples data");
      }

      const data = await response.json();
      console.log("Couples data:", data);

      setCouples(data.data || data || []);
    } catch (err) {
      console.error("Error loading couples:", err);
      setError(err.message);

      // Use mock data on any error
      console.log("Using mock data due to API error");
      setApiFailed(true);
      setCouples(getMockData());
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <svg
          width="10"
          height="16"
          viewBox="0 0 10 16"
          style={{ marginLeft: 4, verticalAlign: "middle" }}
        >
          <g fill="#bbb">
            <polygon points="5,3 8,7 2,7" />
            <polygon points="5,13 2,9 8,9" />
          </g>
        </svg>
      );
    }

    return sortDirection === "asc" ? (
      <svg
        width="10"
        height="16"
        viewBox="0 0 10 16"
        style={{ marginLeft: 4, verticalAlign: "middle" }}
      >
        <g fill="#333">
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" fill="#bbb" />
        </g>
      </svg>
    ) : (
      <svg
        width="10"
        height="16"
        viewBox="0 0 10 16"
        style={{ marginLeft: 4, verticalAlign: "middle" }}
      >
        <g fill="#333">
          <polygon points="5,3 8,7 2,7" fill="#bbb" />
          <polygon points="5,13 2,9 8,9" />
        </g>
      </svg>
    );
  };

  const filteredAndSortedCouples = couples
    .filter(
      (couple) =>
        couple.propertyId?.toLowerCase().includes(search.toLowerCase()) ||
        couple.propertyName?.toLowerCase().includes(search.toLowerCase()) ||
        couple.firstGuardianName
          ?.toLowerCase()
          .includes(search.toLowerCase()) ||
        couple.secondGuardianName?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const aValue = a[sortField] || "";
      const bValue = b[sortField] || "";

      if (sortDirection === "asc") {
        return aValue.toString().localeCompare(bValue.toString());
      } else {
        return bValue.toString().localeCompare(aValue.toString());
      }
    });

  const totalPages = Math.ceil(filteredAndSortedCouples.length / entries);
  const startIndex = (currentPage - 1) * entries;
  const endIndex = startIndex + entries;
  const paginatedCouples = filteredAndSortedCouples.slice(startIndex, endIndex);

  const handleExportCSV = () => {
    try {
      console.log('Starting CSV export...');
      console.log('Visible columns:', visibleColumns);
      console.log('Filtered couples data:', filteredAndSortedCouples);

      // Transform data to match exportUtils format
      const csvData = filteredAndSortedCouples.map((couple) => ({
        propertyId: couple.propertyId || "",
        propertyName: couple.propertyName || "",
        roomName: couple.roomName || "",
        firstGuardianName: couple.firstGuardianName || "",
        firstGuardianStatus: couple.firstGuardianStatus || "",
        secondGuardianName: couple.secondGuardianName || "",
        secondGuardianStatus: couple.secondGuardianStatus || "",
      }));

      // Define columns to include in export
      const columnsToExport = visibleColumns.filter(col => col !== 'actions');
      console.log('Columns to export:', columnsToExport);

      downloadCSV(csvData, "rooms_couples.csv", columnsToExport);
    } catch (error) {
      console.error('Error in CSV export:', error);
      alert('Failed to export CSV: ' + error.message);
    }
  };

  const handleExportPDF = () => {
    try {
      console.log('Starting PDF export...');
      console.log('Visible columns:', visibleColumns);
      console.log('Filtered couples data:', filteredAndSortedCouples);

      // Transform data to match exportUtils format
      const pdfData = filteredAndSortedCouples.map((couple) => ({
        propertyId: couple.propertyId || "",
        propertyName: couple.propertyName || "",
        roomName: couple.roomName || "",
        firstGuardianName: couple.firstGuardianName || "",
        firstGuardianStatus: couple.firstGuardianStatus || "",
        secondGuardianName: couple.secondGuardianName || "",
        secondGuardianStatus: couple.secondGuardianStatus || "",
      }));

      // Define columns to include in export
      const columnsToExport = visibleColumns.filter(col => col !== 'actions');
      console.log('Columns to export:', columnsToExport);

      downloadPDF(
        pdfData,
        "rooms_couples_report.pdf",
        columnsToExport,
        "Rooms Couples Report",
        { enableMultiPage: true, fontSize: 8 }
      );
    } catch (error) {
      console.error('Error in PDF export:', error);
      alert('Failed to export PDF: ' + error.message);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Rooms Couples Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            h1 { color: #333; }
          </style>
        </head>
        <body>
          <h1>Rooms Couples Report</h1>
          <table>
            <thead>
              <tr>
                <th>Property ID</th>
                <th>Property Name</th>
                <th>Room</th>
                <th>Guardian #1</th>
                <th>Guardian #1 Status</th>
                <th>Guardian #2</th>
                <th>Guardian #2 Status</th>
              </tr>
            </thead>
            <tbody>
              ${filteredAndSortedCouples
                .map(
                  (couple) => `
                <tr>
                  <td>${couple.propertyId || ""}</td>
                  <td>${couple.propertyName || ""}</td>
                  <td>${couple.roomName || ""}</td>
                  <td>${couple.firstGuardianName || ""}</td>
                  <td>${couple.firstGuardianStatus || ""}</td>
                  <td>${couple.secondGuardianName || ""}</td>
                  <td>${couple.secondGuardianStatus || ""}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const toggleColumn = (column) => {
    console.log('Toggling column:', column);
    setVisibleColumns((prev) => {
      const newColumns = prev.includes(column)
        ? prev.filter((col) => col !== column)
        : [...prev, column];
      console.log('New visible columns:', newColumns);
      return newColumns;
    });
  };

  // Reusable label style and hover handlers
  const getLabelProps = () => ({
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 12px',
      fontSize: '14px',
      color: '#333',
      cursor: 'pointer',
      margin: 0,
      fontWeight: 'normal',
      transition: 'background-color 0.2s'
    },
    onMouseEnter: (e) => e.target.style.backgroundColor = '#f8f9fa',
    onMouseLeave: (e) => e.target.style.backgroundColor = 'transparent'
  });

  const handleShowCouple = (roomId) => {
    navigate(`/rooms/${roomId}/show`);
  };

  if (loading) return <Loader />;

  return (
    <div className="rooms-couples-laravel">
      {/* Breadcrumbs */}
     
     

      <div className="container-fluid" style={{ padding: 16, backgroundColor: "#FFFFFF" }}>

      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          <a href="/guardians">Guardians</a>
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Couples</span>
        </div>
      </div>


        <div className="row">
          <div className="col-md-12">
            <div className="panel-group" style={{ backgroundColor: "#f9f9f9" }}>
              <div
                className="panel-title title-standard"
                style={{ backgroundColor: "transparent", border: "none" }}
              >
                <h3 id="guardian-status-span">Couples</h3>
              </div>

              <div className="dataTable_wrapper">
                {/* Toolbar */}
                <div
                  className="dt-toolbar"
                  style={{ backgroundColor: "#f9f9f9", border: "none" }}
                >
                  <div className="toolbar-left">
                    <div
                      className="length-menu"
                      style={{ display: "flex", gap: 8 }}
                    >
                      <label>Show</label>

                      <select
                        value={entries}
                        onChange={(e) => {
                          setEntries(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                        <option value={75}>75</option>
                        <option value={100}>100</option>
                      </select>
                      <label>entries</label>
                    </div>
                  </div>

                  <div
                    className="search-box"
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      flex: 1,
                      gap: 4,
                      marginLeft: 24,
                      marginRight: 24,
                    }}
                  >
                    <label>Search:</label>
                    <input
                      type="search"
                      value={search}
                      style={{ flex: 1 }}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search..."
                    />
                  </div>

                  <div className="toolbar-right">
                    <div
                      className="export-buttons"
                      style={{ display: "flex", flexWrap: "nowrap", gap: 8, alignItems: "center" }}
                    >
                      <button
                        className="btn btn-sm btn-default"
                        style={{ borderWidth: 1, borderColor: "#333" }}
                        onClick={handleExportCSV}
                        title="Export as CSV"
                      >
                        Export as CSV
                      </button>
                      <button
                        className="btn btn-sm btn-default"
                        style={{ borderWidth: 1, borderColor: "#333" }}
                        onClick={handleExportPDF}
                        title="Export as PDF"
                      >
                        Export as PDF
                      </button>
                      <button
                        className="btn btn-sm btn-default"
                        style={{ borderWidth: 1, borderColor: "#333" }}
                        onClick={handlePrint}
                        title="Print table"
                      >
                        Print table
                      </button>

                      <div className="column-visibility" style={{ position: 'relative' }}>
                        <button
                          className="btn btn-sm btn-default dropdown-toggle"
                          style={{ borderWidth: 1, borderColor: "#333", position: 'relative' }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowColDropdown(!showColDropdown);
                          }}
                        >
                          Column visibility
                        </button>
                        {showColDropdown && (
                          <div
                            className="dropdown-menu"
                            style={{
                              display: 'block',
                              position: 'absolute',
                              top: '100%',
                              right: 0,
                              zIndex: 1000,
                              backgroundColor: '#fff',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              padding: '8px 0',
                              minWidth: '180px'
                            }}
                          >
                            <label {...getLabelProps()}>
                              <input
                                type="checkbox"
                                checked={visibleColumns.includes("propertyId")}
                                onChange={() => toggleColumn("propertyId")}
                                style={{ margin: 0 }}
                              />
                              Property ID
                            </label>
                            <label {...getLabelProps()}>
                              <input
                                type="checkbox"
                                checked={visibleColumns.includes("propertyName")}
                                onChange={() => toggleColumn("propertyName")}
                                style={{ margin: 0 }}
                              />
                              Property Name
                            </label>
                            <label {...getLabelProps()}>
                              <input
                                type="checkbox"
                                checked={visibleColumns.includes("roomName")}
                                onChange={() => toggleColumn("roomName")}
                                style={{ margin: 0 }}
                              />
                              Room
                            </label>
                            <label {...getLabelProps()}>
                              <input
                                type="checkbox"
                                checked={visibleColumns.includes("firstGuardianName")}
                                onChange={() => toggleColumn("firstGuardianName")}
                                style={{ margin: 0 }}
                              />
                              Guardian #1
                            </label>
                            <label {...getLabelProps()}>
                              <input
                                type="checkbox"
                                checked={visibleColumns.includes("firstGuardianStatus")}
                                onChange={() => toggleColumn("firstGuardianStatus")}
                                style={{ margin: 0 }}
                              />
                              Guardian #1 Status
                            </label>
                            <label {...getLabelProps()}>
                              <input
                                type="checkbox"
                                checked={visibleColumns.includes("secondGuardianName")}
                                onChange={() => toggleColumn("secondGuardianName")}
                                style={{ margin: 0 }}
                              />
                              Guardian #2
                            </label>
                            <label {...getLabelProps()}>
                              <input
                                type="checkbox"
                                checked={visibleColumns.includes("secondGuardianStatus")}
                                onChange={() => toggleColumn("secondGuardianStatus")}
                                style={{ margin: 0 }}
                              />
                              Guardian #2 Status
                            </label>
                            <label {...getLabelProps()}>
                              <input
                                type="checkbox"
                                checked={visibleColumns.includes("actions")}
                                onChange={() => toggleColumn("actions")}
                                style={{ margin: 0 }}
                              />
                              Actions
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Data Table */}
                <table className="table table-hover dt-responsive">
                  <thead>
                    <tr>
                      {visibleColumns.includes("propertyId") && (
                        <th
                          onClick={() => handleSort("propertyId")}
                          style={{ cursor: "pointer" }}
                        >
                          Property ID {getSortIcon("propertyId")}
                        </th>
                      )}
                      {visibleColumns.includes("propertyName") && (
                        <th
                          onClick={() => handleSort("propertyName")}
                          style={{ cursor: "pointer" }}
                        >
                          Property Name {getSortIcon("propertyName")}
                        </th>
                      )}
                      {visibleColumns.includes("roomName") && <th>Room</th>}
                      {visibleColumns.includes("firstGuardianName") && (
                        <th
                          onClick={() => handleSort("firstGuardianName")}
                          style={{ cursor: "pointer" }}
                        >
                          Guardian #1 {getSortIcon("firstGuardianName")}
                        </th>
                      )}
                      {visibleColumns.includes("firstGuardianStatus") && (
                        <th>
                          Guardian #1 <br />
                          Status
                        </th>
                      )}
                      {visibleColumns.includes("secondGuardianName") && (
                        <th
                          onClick={() => handleSort("secondGuardianName")}
                          style={{ cursor: "pointer" }}
                        >
                          Guardian #2 {getSortIcon("secondGuardianName")}
                        </th>
                      )}
                      {visibleColumns.includes("secondGuardianStatus") && (
                        <th>
                          Guardian #2 <br />
                          Status
                        </th>
                      )}
                      {visibleColumns.includes("actions") && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCouples.map((couple) => (
                      <tr key={couple.id}>
                        {visibleColumns.includes("propertyId") && (
                          <td>{couple.propertyId || "-"}</td>
                        )}
                        {visibleColumns.includes("propertyName") && (
                          <td>
                            {couple.propertyName ? (
                              <a href={`/properties/${couple.propertyId}`}>
                                {couple.propertyName}
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                        )}
                        {visibleColumns.includes("roomName") && (
                          <td>{couple.roomName || "-"}</td>
                        )}
                        {visibleColumns.includes("firstGuardianName") && (
                          <td>{couple.firstGuardianName || "-"}</td>
                        )}
                        {visibleColumns.includes("firstGuardianStatus") && (
                          <td>{couple.firstGuardianStatus || "-"}</td>
                        )}
                        {visibleColumns.includes("secondGuardianName") && (
                          <td>{couple.secondGuardianName || "-"}</td>
                        )}
                        {visibleColumns.includes("secondGuardianStatus") && (
                          <td>{couple.secondGuardianStatus || "-"}</td>
                        )}
                        {visibleColumns.includes("actions") && (
                          <td className="action">
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleShowCouple(couple.id)}
                              title="Show Couple"
                            >
                              Show Couple
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="row">
                  <div className="col-sm-5">
                    <div className="dataTables_info">
                      Showing {startIndex + 1} to{" "}
                      {Math.min(endIndex, filteredAndSortedCouples.length)} of{" "}
                      {filteredAndSortedCouples.length} entries
                    </div>
                  </div>
                  <div className="col-sm-7">
                    <div className="dataTables_paginate">
                      <ul className="pagination">
                        <li
                          className={`paginate_button previous ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                        </li>

                        {Array.from(
                          { length: totalPages },
                          (_, i) => i + 1
                        ).map((page) => (
                          <li
                            key={page}
                            className={`paginate_button ${
                              currentPage === page ? "active" : ""
                            }`}
                          >
                            <button onClick={() => setCurrentPage(page)}>
                              {page}
                            </button>
                          </li>
                        ))}

                        <li
                          className={`paginate_button next ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomsCouplesLaravel;
